import { ChartField } from 'src/app/types/chart.model'
import { JOBNUM_FIELD, JOBNUM_LABEL } from 'src/app/types/constants'
import { DashboardJobLookupDto, DashboardJobLookupRoleDto } from 'src/app/types/dto/dashboard-job-lookup.dto'

const jobField = (
  dataField: string,
  displayName: string,
  dataType: ChartField['dataType'] = 'string'
): ChartField => ({ dataField, displayName, dataType, fieldGroup: 'job' })

const IS_ON_HOLD = 'isOnHold'
const IS_ACTIVE = 'isActive'

/** job fields for datasources whose rows can belong to any job (active or not, on hold or not) */
export const CHILD_JOB_FIELDS: ChartField[] = [
  jobField(JOBNUM_FIELD, JOBNUM_LABEL),
  jobField('division', 'Division'),
  jobField('stage', 'Stage'),
  jobField('houseType', 'House Type'),
  jobField('council', 'Council'),
  jobField('tradeRegion', 'Trade Region'),
  jobField('currentActivity', 'Current Activity'),
  jobField(IS_ON_HOLD, 'Is On Hold', 'boolean'),
  jobField(IS_ACTIVE, 'Is Active', 'boolean'),
]

/** cashflow only includes active jobs, so Is Active can never vary */
export const CASHFLOW_JOB_FIELDS: ChartField[] = CHILD_JOB_FIELDS.filter(
  (field) => field.dataField !== IS_ACTIVE
)

/** forecasts only include active, sold jobs that aren't on hold */
export const FORECAST_JOB_FIELDS: ChartField[] = [
  ...CHILD_JOB_FIELDS.filter((field) => field.dataField !== IS_ACTIVE && field.dataField !== IS_ON_HOLD),
  jobField('contractSignedDate', 'Contract Signed Date', 'date'),
]

export interface JobLookup {
  roles: DashboardJobLookupRoleDto[]
  valuesByJobNumber: Map<string, Record<string, unknown>>
  /** job numbers still missing after a refetch - stops repeated refetches for the same rows */
  knownMissingJobNumbers: Set<string>
}

export function buildJobLookup(dto: DashboardJobLookupDto): JobLookup {
  const keyByRoleId = new Map(dto.roles.map((role) => [role.roleId, role.key]))
  const valuesByJobNumber = new Map<string, Record<string, unknown>>()

  for (const job of dto.jobs) {
    const values: Record<string, unknown> = {
      division: job.division,
      stage: job.stage,
      houseType: job.houseType,
      council: job.council,
      tradeRegion: job.tradeRegion,
      currentActivity: job.currentActivity,
      isOnHold: job.isOnHold,
      isActive: job.isActive,
    }
    dto.roles.forEach((role) => (values[role.key] = null))
    job.roles.forEach((assignment) => {
      const key = keyByRoleId.get(assignment.roleId)
      if (key) values[key] = assignment.name
    })
    valuesByJobNumber.set(job.jobNumber, values)
  }

  return { roles: dto.roles, valuesByJobNumber, knownMissingJobNumbers: new Set() }
}

/** overwrites each row's job fields and roles with the job's current values */
export function applyJobLookup<T extends object>(rows: T[], lookup: JobLookup): T[] {
  return rows.map((row) => {
    const jobNumber = (row as { jobNumber?: string | null }).jobNumber
    const values = jobNumber ? lookup.valuesByJobNumber.get(jobNumber) : undefined
    return values ? { ...row, ...values } : row
  })
}

export function getMissingJobNumbers(rows: object[], lookup: JobLookup): string[] {
  const missing = new Set<string>()
  for (const row of rows) {
    const jobNumber = (row as { jobNumber?: string | null }).jobNumber
    if (
      jobNumber &&
      !lookup.valuesByJobNumber.has(jobNumber) &&
      !lookup.knownMissingJobNumbers.has(jobNumber)
    ) {
      missing.add(jobNumber)
    }
  }
  return Array.from(missing)
}

export function buildRoleField(role: DashboardJobLookupRoleDto): ChartField {
  return {
    dataField: role.key,
    displayName: role.isActive ? role.description : `${role.description} (disabled)`,
    dataType: 'string',
    isPersonField: true,
    fieldGroup: 'jobRole',
  }
}

/** active roles always show; disabled roles only while still assigned to a job */
export function isRoleListed(role: DashboardJobLookupRoleDto): boolean {
  return role.isActive || role.isAssigned
}
